import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Send, RotateCcw, Clock, Loader2, History } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../utils/api';

import { formatDateMonthDay } from '../../../utils/timeFormat';

/**
 * PO4 (Sprint 2026-08-05) — Invitations tab with reminder tracking.
 *
 * Every invitation row now shows:
 *   • Reminder count + last reminded timestamp (if > 0)
 *   • Resend button (bumps reminder_count without creating duplicates)
 *   • History icon (opens dialog with the full reminder audit trail)
 */
const InvitationsTab = ({ sentInvitations, onRefresh }) => {
  const [resending, setResending] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState(null);

  const resend = async (inv) => {
    setResending(inv.id);
    try {
      await api.post(`/invitations/${inv.id}/resend`, {
        channels: ['email', 'sms', 'in_app'],
      });
      toast.success(`Reminder sent to ${inv.first_name} ${inv.last_name}`);
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to resend invitation');
    } finally {
      setResending(null);
    }
  };

  const openHistory = async (inv) => {
    setHistoryOpen(inv);
    setHistoryLoading(true);
    setHistoryData(null);
    try {
      const res = await api.get(`/invitations/${inv.id}/history`);
      setHistoryData(res.data);
    } catch (e) {
      toast.error('Failed to load invitation history');
      setHistoryOpen(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const canResend = (inv) => !['accepted', 'declined', 'expired'].includes(inv.status);

  return (
    <>
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl text-gray-900">Sent Invitations</CardTitle>
        </CardHeader>
        <CardContent>
          {sentInvitations.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Send className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p>No invitations sent yet. Invite talent from your Favorites or Groups.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sentInvitations.map((inv) => {
                const statusColor = {
                  pending: 'bg-amber-50 text-amber-700 border-amber-200',
                  accepted: 'bg-green-50 text-green-700 border-green-200',
                  declined: 'bg-red-50 text-red-700 border-red-200',
                }[inv.status] || 'bg-gray-50 text-gray-700 border-gray-200';

                return (
                  <div
                    key={inv.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gray-50 rounded-lg gap-2"
                    data-testid={`invitation-${inv.id}`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {inv.profile_photo_url ? (
                        <img src={inv.profile_photo_url} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#03DFFC]/10 flex items-center justify-center text-[#03DFFC] text-sm font-medium shrink-0">
                          {inv.first_name?.[0]}{inv.last_name?.[0]}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">{inv.first_name} {inv.last_name}</p>
                        <p className="text-xs text-gray-500 truncate">{inv.gig_role_name} — {inv.venue_name}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className="text-xs text-gray-400">via {inv.source}</span>
                          {inv.is_priority && (
                            <Badge className="text-[10px] bg-yellow-100 text-yellow-800 border-yellow-300 px-1 py-0">
                              Priority
                            </Badge>
                          )}
                          <span className="text-xs text-gray-400">
                            • {inv.created_at ? formatDateMonthDay(inv.created_at) : ''}
                          </span>
                          {/* PO4 — reminder history summary */}
                          {inv.reminder_count > 0 && (
                            <Badge
                              className="text-[10px] bg-cyan-50 text-cyan-800 border-cyan-200 border px-1.5 py-0 inline-flex items-center gap-1"
                              title={inv.last_reminded_at ? `Last reminded ${new Date(inv.last_reminded_at).toLocaleString()}` : ''}
                              data-testid={`reminder-count-${inv.id}`}
                            >
                              <RotateCcw className="w-2.5 h-2.5" />
                              {inv.reminder_count} reminder{inv.reminder_count === 1 ? '' : 's'}
                            </Badge>
                          )}
                          {inv.last_reminded_at && (
                            <span className="text-[10px] text-gray-400 inline-flex items-center gap-0.5">
                              <Clock className="w-2.5 h-2.5" />
                              {formatDateMonthDay(inv.last_reminded_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <Badge className={`text-xs border ${statusColor}`} data-testid={`inv-status-${inv.id}`}>
                        {inv.status === 'pending' ? 'No Response' : inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                      </Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openHistory(inv)}
                        className="text-xs h-7 px-2"
                        data-testid={`inv-history-btn-${inv.id}`}
                        title="View reminder history"
                      >
                        <History className="w-3 h-3" />
                      </Button>
                      {canResend(inv) && (
                        <Button
                          size="sm"
                          onClick={() => resend(inv)}
                          disabled={resending === inv.id}
                          className="text-xs h-7 px-2 bg-cyan-600 hover:bg-cyan-700 text-white"
                          data-testid={`inv-resend-btn-${inv.id}`}
                        >
                          {resending === inv.id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <><RotateCcw className="w-3 h-3 mr-1" /> Resend</>}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* PO4 — Invitation history dialog */}
      <Dialog open={!!historyOpen} onOpenChange={(o) => !o && setHistoryOpen(null)}>
        <DialogContent data-testid="invitation-history-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Invitation History{historyOpen && ` — ${historyOpen.first_name} ${historyOpen.last_name}`}
            </DialogTitle>
          </DialogHeader>
          {historyLoading ? (
            <div className="text-center py-6"><Loader2 className="w-6 h-6 animate-spin text-cyan-600 inline" /></div>
          ) : historyData ? (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              <div className="text-xs text-gray-500 bg-gray-50 rounded p-2 border">
                <div>
                  First invited:{' '}
                  <span className="font-medium text-gray-900">
                    {historyData.first_invited_at ? new Date(historyData.first_invited_at).toLocaleString() : '—'}
                  </span>
                </div>
                <div>
                  Reminders sent: <span className="font-medium text-cyan-700">{historyData.reminder_count}</span>
                </div>
                <div>
                  Status: <span className="font-medium">{historyData.status || 'pending'}</span>
                  {historyData.responded_at && (
                    <> — responded {new Date(historyData.responded_at).toLocaleString()}</>
                  )}
                </div>
              </div>
              {historyData.events.length === 0 ? (
                <div className="text-center py-4 text-gray-500 text-sm">
                  No reminders logged yet. The original invitation was sent at first_invited_at.
                </div>
              ) : (
                historyData.events.map((e) => (
                  <div key={e.id} className="border border-gray-200 rounded p-2 text-xs"
                    data-testid={`history-event-${e.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-gray-500">{new Date(e.created_at).toLocaleString()}</span>
                      {e.channels.map((c) => (
                        <Badge key={c} className="text-[10px] bg-cyan-50 text-cyan-800 border-cyan-200 border px-1 py-0">
                          {c}
                        </Badge>
                      ))}
                    </div>
                    {e.actor && <div className="text-gray-500 mt-0.5">by {e.actor}</div>}
                    {e.note && <div className="text-gray-700 mt-1 italic">&ldquo;{e.note}&rdquo;</div>}
                  </div>
                ))
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(null)}
              data-testid="history-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InvitationsTab;
